import {
  appConfig,
  Button,
  FormDropdown,
  FormInput,
  InvisibleSubmit,
  makeValidable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NotesBanner,
  PageLoadingSpinner,
  SimpleModal
} from '@ami/react/core';
import PropTypes from 'prop-types';
import React from 'react';
import { DealerBranch } from '../../dealer-branches/dealerBranch';
import { DealerGroup } from '../../dealer-groups/dealerGroup';
import { DealerUserFilter } from '../../dealer-users/dealerUserFilter';
import { dealerUserService } from '../../dealer-users/dealerUserService';
import NotificationSettings from '../../notificationSettings';
import { NotificationSettingsComponent } from '../../NotificationSettingsComponent';
import { CopyQuoteData } from '../../quotes/otherQuoteEntities';
import { Quote } from '../../quotes/quote';
import QuoteView from '../../quotes/view/quoteView';
import { Team } from '../../teams/team';
import { User } from '../../users/user';

const CustomLineItemWarning = ({ quoteHasCustomLineItems }) =>
  quoteHasCustomLineItems ? (
    <div className="alert alert-warning" role="alert">
      <strong>WARNING:</strong> This quote has custom line items. These line
      items will not be copied to the new quote.
    </div>
  ) : null;

const CustomLeadTimesWarning = ({ quoteHasOverriddenLeadTimes }) =>
  quoteHasOverriddenLeadTimes ? (
    <div className="alert alert-warning" role="alert">
      <strong>WARNING:</strong> This quote has products with custom lead times.
      These custom lead times will not be copied to the new quote.
    </div>
  ) : null;

const FormData = makeValidable(
  ({
    item,
    quoteHasCustomLineItems,
    quoteHasOverriddenLeadTimes,
    branches,
    dealerUsers,
    save,
    updateItem,
    onDealerBranchChange,
    dealerGroup,
    dealerBranch,
    notificationSettings,
    amiUsers,
    dealerBranchSalesTeam,
    updateNotificationSettings,
    quote,
  }) => {
    const dealerUser = dealerUsers.find((x) => x.id === item.dealerUserId);
    return (
      <form className="container" onSubmit={save}>
        {appConfig.isDealerApp && (
          <CustomLineItemWarning
            quoteHasCustomLineItems={quoteHasCustomLineItems}
          />
        )}
        <CustomLeadTimesWarning
          quoteHasOverriddenLeadTimes={quoteHasOverriddenLeadTimes}
        />
        <FormInput
          label="Name"
          placeholder="Quote name..."
          value={item.name}
          onChange={(e) => updateItem({ name: e.target.value })}
          required
        />
        <FormInput
          type="textarea"
          textAreaAutoRows={true}
          label="Notes"
          placeholder="Quote notes..."
          value={item.notes}
          onChange={(e) => updateItem({ notes: e.target.value })}
        />
        { item.notes.trim() && (
          <NotesBanner />
        )}
        <FormDropdown
          placeholder="Select a branch..."
          label="Branch"
          items={branches}
          selectedId={item.dealerBranchId}
          onChange={(dealerBranchId) => onDealerBranchChange(dealerBranchId)}
          required
        />
        <FormDropdown
          placeholder="Select a dealer contact..."
          label="New Dealer Contact"
          items={dealerUsers}
          selectedId={item.dealerUserId}
          onChange={(dealerUserId) => updateItem({ dealerUserId })}
          required
        />
        {appConfig.isManagementApp &&
          item.dealerBranchId &&
          item.dealerUserId && (
            <div className="row">
              <div className="col">
                <NotificationSettingsComponent
                  isEmailCommunicationEnabled={
                    dealerGroup.isEmailCommunicationEnabled
                  }
                  dealerGroupId={dealerGroup.id}
                  dealerGroupUsers={dealerUsers}
                  dealerUser={dealerUser}
                  amiUsers={amiUsers.filter(
                    (x) =>
                      x.id === quote.salesPersonId ||
                      x.id === quote.territoryManagerInformation.userId ||
                      dealerBranchSalesTeam.amiUserIds.includes(x.id) ||
                      quote.subscribedAmiUserIds.includes(x.id)
                  )}
                  isEditMode={true}
                  notificationSettings={notificationSettings}
                  onNotificationSettingsUpdate={updateNotificationSettings}
                  entityName="Quote"
                  defaultDealerContactIds={dealerBranch.defaultDealerContactIds}
                  enabledNotificationSchedule={false}
                />
              </div>
            </div>
          )}
        <InvisibleSubmit />
      </form>
    );
  }
);

export default class CopyQuoteModal extends React.Component {
  constructor(props) {
    super(props);
    this.formDataRef = React.createRef();
    this.changeBranch = this.changeBranch.bind(this);
    this.updateNotificationSettings =
      this.updateNotificationSettings.bind(this);
  }

  state = {
    show: false,
    showValidations: false,
    dealerBranches: [],
    dealerUsers: [],
    item: null,
    copying: false,
    // The properties below are used in the notification settings modal.
    dealerBranch: null,
    dealerGroup: null,
    amiUsers: [],
    dealerBranchSalesTeam: null,
    notificationSettings: new NotificationSettings(),
  };

  static propTypes = {
    quote: PropTypes.instanceOf(QuoteView).isRequired,
    quoteHasCustomLineItems: PropTypes.bool.isRequired,
    quoteHasOverriddenLeadTimes: PropTypes.bool.isRequired,
    onSaved: PropTypes.func.isRequired,
  };

  async componentDidUpdate(prevProps, prevState) {
    if (!prevState.show && this.state.show) {
      const dealerBranches = await DealerBranch.search();
      const validBranches = dealerBranches.filter(
        (x) => x.isoCurrencySymbol === this.props.quote.isoCurrencySymbol
      );
      this.setState({
        dealerBranches: validBranches,
      });
    }
  }

  async copy() {
    const copyQuoteData = new CopyQuoteData();
    copyQuoteData.id = this.props.quote.id;
    copyQuoteData.name = this.props.quote.name;
    copyQuoteData.notes = this.props.quote.notes;
    copyQuoteData.dealerBranchId =
      this.props.quote.dealerInformation.dealerBranchId;
    copyQuoteData.dealerUserId =
      this.props.quote.dealerInformation.dealerUserId;

    if (appConfig.isManagementApp) {
      const dealerBranch = await DealerBranch.getById(
        copyQuoteData.dealerBranchId
      );
      this.setState({
        show: true,
        showValidations: false,
        item: copyQuoteData,
        dealerBranch,
        dealerUsers: await dealerUserService.search(
          new DealerUserFilter({ dealerBranchId: copyQuoteData.dealerBranchId })
        ),
        dealerGroup: await DealerGroup.getById(dealerBranch.dealerGroupId),
        amiUsers: await User.search(),
        dealerBranchSalesTeam: await Team.getById(dealerBranch.salesTeamId),
      });
    } else {
      this.setState({
        show: true,
        showValidations: false,
        item: copyQuoteData,
        dealerUsers: await dealerUserService.search(
          new DealerUserFilter({ dealerBranchId: copyQuoteData.dealerBranchId })
        ),
      });
    }
  }

  updateItem = (updateObject) =>
    this.setState({
      item: new CopyQuoteData({
        ...this.state.item,
        ...updateObject,
      }),
    });

  async changeBranch(dealerBranchId) {
    this.updateItem({ dealerBranchId, dealerUserId: null });

    const dealerUsers = dealerBranchId
      ? await dealerUserService.search(new DealerUserFilter({ dealerBranchId }))
      : [];

    this.setState({ dealerUsers });

    if (appConfig.isManagementApp)
      await this.setStateForNotificationSettings(dealerBranchId);
  }

  async setStateForNotificationSettings(dealerBranchId) {
    if (dealerBranchId) {
      const dealerBranch = await DealerBranch.getById(dealerBranchId);
      this.setState({
        dealerBranch,
        dealerGroup: await DealerGroup.getById(dealerBranch.dealerGroupId),
        amiUsers: await User.search(),
        dealerBranchSalesTeam: await Team.getById(dealerBranch.salesTeamId),
      });
    } else {
      this.setState({
        dealerBranch: null,
        dealerGroup: null,
        amiUsers: [],
        dealerBranchSalesTeam: null,
      });
    }
  }

  hide = () => this.setState({ show: false });

  save = (e) => {
    if (e) e.preventDefault();
    this.setState({ showValidations: true }, async () => {
      if (!this.formDataRef.current.isValid()) return;
      this.setState({ copying: true });
      const id = await Quote.copy(
        this.props.quote.id,
        this.state.item,
        this.state.notificationSettings
      )
        .catch(() => { })
        .finally(() => this.setState({ copying: false }));
      if (id) {
        this.props.onSaved(id);
        this.hide();
      }
    });
  };

  updateNotificationSettings = (notificationSettings) => {
    this.setState({ notificationSettings });
  };

  render() {
    const {
      show,
      dealerBranches,
      item,
      dealerUsers,
      showValidations,
      copying,
      notificationSettings,
      dealerGroup,
      dealerBranch,
      amiUsers,
      dealerBranchSalesTeam,
    } = this.state;
    if (!show) return null;

    const branches = dealerBranches.map((x) => ({
      id: x.dealerBranchId,
      label: x.dealerBranchName,
    }));

    return (
      <SimpleModal show={show} size="lg">
        <ModalHeader title="Copy Quote" />
        {(!dealerBranches || dealerBranches.length === 0) && (
          <div className="mb-4">
            <PageLoadingSpinner />
          </div>
        )}
        {dealerBranches && dealerBranches.length > 0 && (
          <React.Fragment>
            <ModalBody>
              <FormData
                item={item}
                quoteHasCustomLineItems={this.props.quoteHasCustomLineItems}
                quoteHasOverriddenLeadTimes={
                  this.props.quoteHasOverriddenLeadTimes
                }
                branches={branches}
                dealerUsers={dealerUsers}
                save={this.save}
                updateItem={this.updateItem}
                onDealerBranchChange={this.changeBranch}
                ref={this.formDataRef}
                showValidations={showValidations}
                notificationSettings={notificationSettings}
                dealerGroup={dealerGroup}
                dealerBranch={dealerBranch}
                amiUsers={amiUsers}
                dealerBranchSalesTeam={dealerBranchSalesTeam}
                updateNotificationSettings={this.updateNotificationSettings}
                quote={this.props.quote}
              />
            </ModalBody>
            <ModalFooter>
              <button className="btn btn-secondary" onClick={this.hide}>
                Cancel
              </button>
              <Button
                label="Save"
                className="btn btn-primary"
                onClick={this.save}
                showSpinner={copying}
              />
            </ModalFooter>
          </React.Fragment>
        )}
      </SimpleModal>
    );
  }
}
